import React, { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { baseURL, useFetch } from "../../custom/api";
import "../../styles/Achievements.css";

// Module-level (not component state) so an achievement id is only ever
// toasted once per app session, however it was discovered - either via the
// GET achievements/unseen poll below, or via a `newly_unlocked` array that
// some other mutating call (cooked/mark, calendar/add, challenge checkin,
// PUT account, community post...) handed back directly.
const shownIds = new Set();

function AchievementUnlockToastCard({ achievement }) {
  const icon = achievement.icon && achievement.icon.trim() ? achievement.icon : "🏆";
  return (
    <div className={`achievementToastCard achRarity-${achievement.rarity || "common"}`}>
      <span className="achievementToastIcon" aria-hidden="true">
        {icon}
      </span>
      <div className="achievementToastText">
        <p className="achievementToastTitle yekan">دستاورد جدید باز شد!</p>
        <p className="achievementToastName yekan">{achievement.name}</p>
      </div>
    </div>
  );
}

async function markSeen(ids) {
  if (!ids || ids.length === 0) return;
  const token = localStorage.getItem("token");
  if (!token) return;
  try {
    await fetch(`${baseURL}/achievements/seen`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ achievement_ids: ids }),
    });
  } catch (e) {
    // best-effort - if this fails the next unseen-poll will just show it again
  }
}

// Call this with any `newly_unlocked` array coming back from a mutating
// endpoint, or with the `unseen` array from GET achievements/unseen. Shows a
// celebratory toast per achievement (staggered so several don't all stack at
// once) and marks them seen so they never show again.
export function notifyUnlocked(achievements) {
  if (!achievements || achievements.length === 0) return;
  const fresh = achievements.filter(
    (a) => a && a.achievement_id != null && !shownIds.has(a.achievement_id)
  );
  if (fresh.length === 0) return;
  fresh.forEach((a) => shownIds.add(a.achievement_id));

  fresh.forEach((a, i) => {
    setTimeout(() => {
      toast(<AchievementUnlockToastCard achievement={a} />, { autoClose: 6000 });
    }, i * 650);
  });

  markSeen(fresh.map((a) => a.achievement_id));
}

// Mount once, high in the tree (App.js), only while logged in. Catches any
// achievement unlocked server-side that the current session didn't already
// see live via a `newly_unlocked` response.
export default function AchievementUnlockWatcher() {
  const { myFetch } = useFetch();
  const ranRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        const resp = await myFetch("achievements/unseen", { method: "GET" });
        const r = await resp.json();
        if (r && r.unseen) notifyUnlocked(r.unseen);
      } catch (e) {
        // ignore - non-critical background check
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
